import { EcspLegalComplianceProfileConfig } from '../../components/account/legal/complianceProfile/ecspComplianceProfile'
import { EsacLegalComplianceProfileConfig } from '../../components/account/legal/complianceProfile/esacComplianceProfile'
import { EcspPersonalComplianceProfileConfig } from '../../components/account/personal/complianceProfile/ecspComplianceProfile'
import { EsacPersonalComplianceProfileConfig } from '../../components/account/personal/complianceProfile/esacComplianceProfile'
import { ENUM_NATURAL_PROFILE_GENDER_MAPPING, NaturalProfileGender, NavigationItemParent } from '../../typings'
import { LegalAccountFormStore } from '../account/useAccountFormLegal'
import { PersonalAccountFormStore } from '../account/useAccountFormPersonal'
import { PersonalAccountPage } from '../account/useAccountNavigationPersonal'
import { LegalAccountPage } from '../account/useAccountNavigationLegal'
import { format, parseISO } from 'date-fns'
import { computed, WritableComputedRef } from 'vue'
import { getSelectableCountryCodes } from '../../data'
import { i18n } from '../../localization'
import { openConfirmDialog } from '../useDialog'

/**
 * neat promise handler function ✨
 *
 * Source: fireship on YouTube (https://www.youtube.com/shorts/ITogH7lJTyE)
 *
 * Example:
 * const [data, error] = await promiseHandler(<insertAnyPromiseFn>);
 * if(error) {
 *     handle error
 * } else {
 *     handle data or whatever
 * }
 * @param promise
 */
export async function promiseHandler<T = any, E = Error>(promise: Promise<T>): Promise<[T, E]> {
    // TODO: check if signature typing is correct
    try {
        const data: T = await promise
        return [data, null]
    } catch (error) {
        console.error(error)
        return [null, error]
    }
}

export const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
    })

export function getIconForPage(navigationItems: NavigationItemParent[], page: LegalAccountPage | PersonalAccountPage) {
    for (const navigationItem of navigationItems) {
        if (navigationItem.icon && navigationItem.component === page) {
            return navigationItem.icon
        }
        if (navigationItem.children) {
            for (const navigationItemElement of navigationItem.children) {
                if (navigationItemElement.component === page) {
                    return navigationItemElement.icon
                }
            }
        }
    }
    return null
}

export function replaceCurrencyWithCurrencySymbol(currency: string): string {
    let symbol = currency
    if (currency === 'EUR') {
        symbol = '€'
    }
    return symbol
}

export function roundToPrecision(value: number, decimals: number): number {
    const factor = 10 ** decimals
    return Math.round(value * factor) / factor
}

export function amountFormatter(amount: string | number): string {
    return parseFloat(typeof amount === 'string' ? amount : amount.toString())
        .toFixed(2)
        .replace('.', ',')
}

export function enumToSelectOptions(enumObject: any, enumKey: string) {
    return Object.keys(enumObject).map((key) => {
        return {
            key: enumObject[key],
            label: i18n.global.t(enumKey + '.' + enumObject[key]),
        }
    })
}

export const kycProviderInternal = computed(() => window.condaKycSettings.kyc_provider === 'internal')
export const kycProviderExternal = computed(() => window.condaKycSettings.kyc_provider === 'external')

export const formatDate = (value: string | Date): string => (value ? format(parseDate(value), 'dd.MM.yyyy') : '––')

export const formatTime = (value) => (value ? format(parseDate(value), 'HH:mm') : '––')

export const formatDateTime = (value: string | Date): string =>
    value ? format(parseDate(value), 'dd.MM.yyyy HH:mm') : '––'

export function formatCountry(countryCode): string {
    return getSelectableCountryCodes().find((country) => country.key === countryCode)?.label
}

export function formatGender(gender: NaturalProfileGender): string {
    return ENUM_NATURAL_PROFILE_GENDER_MAPPING[gender]
}

export function parseDate(date: string | Date): Date {
    if (date instanceof Date) {
        return date
    }
    return parseISO(date)
}

/**
 * currency Formatter
 * Note: only use for fiat currencies (EUR, CHF, USD) TODO: solution for crypto
 * @param amount
 * @param currency
 * @param maximumFractionDigits
 */
export function formatCurrency(amount: number, currency = 'EUR', maximumFractionDigits = 2) {
    if (currency === '&euro;') {
        currency = 'EUR'
    }
    // return 'test'
    const currencyFormatter = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currency || 'EUR',
        // These options are needed to round to whole numbers if that's what you want.
        //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        maximumFractionDigits, // (causes 2500.99 to be printed as $2,501)
    })
    return currencyFormatter.format(amount)
}

export function useOpenCloseForm() {
    const openForm = async (
        model: any,
        currentForm: WritableComputedRef<string>,
        complianceProfile:
            | EsacPersonalComplianceProfileConfig
            | EsacLegalComplianceProfileConfig
            | EcspPersonalComplianceProfileConfig
            | EcspLegalComplianceProfileConfig,
        accountFormStore: LegalAccountFormStore | PersonalAccountFormStore,
        emit: (...args: any[]) => void,
        allowSave = true,
        pageKey: string
    ) => {
        let confirmed = true
        if (currentForm.value !== null) {
            confirmed = await closeForm(model, currentForm, complianceProfile, accountFormStore)
        }
        if (confirmed) {
            if (allowSave) {
                currentForm.value = pageKey
            } else {
                emit('navigateToForm', pageKey)
            }
        }
    }
    const closeForm = async (
        model: any,
        currentForm: WritableComputedRef<string>,
        complianceProfile:
            | EsacPersonalComplianceProfileConfig
            | EsacLegalComplianceProfileConfig
            | EcspPersonalComplianceProfileConfig
            | EcspLegalComplianceProfileConfig,
        accountFormStore: LegalAccountFormStore | PersonalAccountFormStore
    ) => {
        if (complianceProfile[currentForm.value]?.clearValuesOnClose === true && accountFormStore.isDirty) {
            const confirm = await openConfirmDialog(
                i18n.global.t('compliance_profile.discard_header'),
                i18n.global.t('compliance_profile.discard_description'),
                {
                    type: 'Warning',
                    cancelText: i18n.global.t('compliance_profile.edit_data'),
                    confirmText: i18n.global.t('compliance_profile.discard_data'),
                }
            )
            if (!confirm) {
                return false
            } else {
                clearValuesOnClose(model, currentForm, complianceProfile, accountFormStore)
            }
        }
        currentForm.value = null
        return true
    }

    return {
        openForm,
        closeForm,
    }
}

export function useSaveForm() {
    const saveForm = async (
        currentForm: WritableComputedRef<string>,
        complianceProfile:
            | EsacPersonalComplianceProfileConfig
            | EsacLegalComplianceProfileConfig
            | EcspPersonalComplianceProfileConfig
            | EcspLegalComplianceProfileConfig,
        accountFormStore: LegalAccountFormStore | PersonalAccountFormStore,
        allowSave = true,
        setIsValid = () => null
    ) => {
        if (allowSave) {
            if (complianceProfile[currentForm.value].requireConfirm) {
                const confirm = await openConfirmDialog(
                    i18n.global.t('compliance_profile.save_header'),
                    i18n.global.t('compliance_profile.save_description'),
                    {
                        type: 'Success',
                        confirmText: i18n.global.t('save'),
                    }
                )
                if (!confirm) {
                    return
                }
            }

            const [data, error] = await promiseHandler(accountFormStore.submit())
            if (data) {
                currentForm.value = null
                await setIsValid()
            }
        }
    }

    return {
        saveForm,
    }
}

function clearValuesOnClose(
    model: any,
    currentForm: WritableComputedRef<string>,
    complianceProfile:
        | EsacPersonalComplianceProfileConfig
        | EsacLegalComplianceProfileConfig
        | EcspPersonalComplianceProfileConfig
        | EcspLegalComplianceProfileConfig,
    accountFormStore: LegalAccountFormStore | PersonalAccountFormStore
) {
    const formKey = complianceProfile[currentForm.value].validationKey
    for (const key in model[complianceProfile[currentForm.value].validationKey]) {
        accountFormStore.$state.currentForm.resetField(`${formKey}.${key}`)
        model[complianceProfile[currentForm.value].validationKey][key] =
            accountFormStore.$state.currentForm.values[formKey][key]
    }
}
